import axios from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

const AuthContext = createContext();

// Get API URL from environment - base URL without /api
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_URL = `${BASE_URL}/api`;

// Debug logging
console.log('🔧 AuthContext - Environment Info:');
console.log('🔧 NODE_ENV:', process.env.NODE_ENV);
console.log('🔧 REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('🔧 BASE_URL:', BASE_URL);
console.log('🔧 Final API_URL:', API_URL);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Configure axios defaults
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Check authentication status on app load
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setLoading(false);
        return;
      }

      console.log('🔧 AuthContext - Checking auth with URL:', `${API_URL}/auth/me`);
      const response = await axios.get(`${API_URL}/auth/me`);
      setAdmin(response.data.user);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('❌ AuthContext - Auth check failed:', error);
      console.error('❌ AuthContext - Error status:', error.response?.status);
      console.error('❌ AuthContext - Error message:', error.response?.data);
      localStorage.removeItem('adminToken');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      console.log('🔧 AuthContext - Login attempt with URL:', `${API_URL}/auth/login`);
      
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
        userType: 'admin'
      });

      const { token, user } = response.data;
      
      localStorage.setItem('adminToken', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setAdmin(user);
      setIsAuthenticated(true);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.error('❌ AuthContext - Login error:', error);
      console.error('❌ AuthContext - Error response:', error.response);
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('adminToken');
      delete axios.defaults.headers.common['Authorization'];
      setAdmin(null);
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
    }
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      await axios.post(`${API_URL}/auth/forgot-password`, { email, userType: 'admin' });
      toast.success('Password reset email sent!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send reset email';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setLoading(true);
      await axios.put(`${API_URL}/auth/reset-password/${token}`, { password, userType: 'admin' });
      toast.success('Password reset successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password reset failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      await axios.put(`${API_URL}/auth/password`, {
        currentPassword,
        newPassword
      });
      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const response = await axios.put(`${API_URL}/auth/profile`, profileData);
      setAdmin(response.data.user);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // API Methods for admin operations
  const apiCall = async (method, url, data = null) => {
    try {
      let fullUrl;
      
      if (url.startsWith('http')) {
        // If URL starts with http, use it as is
        fullUrl = url;
      } else if (url.startsWith('/api/')) {
        // If URL starts with /api/, remove the /api prefix since API_URL already includes it
        fullUrl = `${API_URL}${url.substring(4)}`;
      } else {
        // Otherwise, prepend API_URL
        fullUrl = `${API_URL}${url}`;
      }
      
      console.log('🔧 AuthContext - API call:', method, fullUrl);
      console.log('🔧 AuthContext - Original URL:', url);
      
      const config = {
        method,
        url: fullUrl,
        ...(data && { data })
      };
      const response = await axios(config);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ AuthContext - API call error:', error);
      const message = error.response?.data?.message || 'Operation failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    // State
    admin,
    loading,
    isAuthenticated,
    
    // Auth methods
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    updateProfile,
    checkAuth,
    
    // Utility method
    apiCall
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 